import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { customFieldsService } from '../api/custom-fields.service';
import { CUSTOM_FIELD_KEYS } from './queryKeys';
import type { EditFieldOptionInput } from '../schemas';

/**
 * Updates a single `Select`/`MultiSelect` option in place — `PUT .../fields/{fieldId}/options/{optionId}`.
 *
 * @remarks
 * Editing `value` (not `name`) breaks any task's existing `FieldValue` that still
 * references the old value — the UI should steer users toward renaming (`name`) once
 * the field may already have data. See `frontend-spec-custom-project-fields.md` §4.1.
 */
export function useUpdateFieldOption(projectId: string, fieldId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: ({ optionId, payload }: { optionId: string; payload: EditFieldOptionInput }) =>
      customFieldsService.updateFieldOption(projectId, fieldId, optionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CUSTOM_FIELD_KEYS.byProject(activeOrganizationId ?? '', projectId),
      });
    },
  });
}

export function useDeleteFieldOption(projectId: string, fieldId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (optionId: string) =>
      customFieldsService.deleteFieldOption(projectId, fieldId, optionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CUSTOM_FIELD_KEYS.byProject(activeOrganizationId ?? '', projectId),
      });
    },
  });
}
